import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Dropdown, Button, Offcanvas } from 'react-bootstrap';
import { BsList } from 'react-icons/bs';
import { resolveProfilePhoto } from '../utils/profilePhoto';
import GstApiDueNotice from './GstApiDueNotice';
import {
  clearStoredSession,
  getStoredUser,
  redirectToLogin,
  SESSION_USER_UPDATED_EVENT,
} from '../utils/sessionUser';
import './PanelMobileChrome.css';

const ContractorMainLayout = () => {
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [user, setUser] = useState(() => getStoredUser());
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const syncUser = () => setUser(getStoredUser());
    window.addEventListener(SESSION_USER_UPDATED_EVENT, syncUser);
    window.addEventListener('storage', syncUser);
    return () => {
      window.removeEventListener(SESSION_USER_UPDATED_EVENT, syncUser);
      window.removeEventListener('storage', syncUser);
    };
  }, []);

  const handleLogout = () => {
    clearStoredSession();
    redirectToLogin();
  };

  const routeTitles = {
    '/contractor/dashboard': 'Dashboard',
    '/contractor/profile': 'Profile',
    '/contractor/work-entry': 'Work Entry',
    '/contractor/work-history': 'Work History',
    '/contractor/payment-history': 'Payment History',
  };

  const menuItems = [
    { path: '/contractor/dashboard', label: 'Dashboard', icon: 'space_dashboard' },
    { path: '/contractor/work-entry', label: 'Add Work', icon: 'edit_note' },
    { path: '/contractor/work-history', label: 'Work History', icon: 'history' },
    { path: '/contractor/payment-history', label: 'Payment History', icon: 'receipt_long' },
    { path: '/contractor/profile', label: 'Profile', icon: 'account_circle' },
  ];

  const handleNav = (path) => {
    navigate(path);
    setSidebarVisible(false);
  };

  const currentPath = location.pathname;
  const isDashboardRoute =
    currentPath === '/contractor' ||
    currentPath === '/contractor/' ||
    currentPath === '/contractor/dashboard';
  const isBottomActive = (prefixes = []) =>
    prefixes.some((p) => currentPath === p || currentPath.startsWith(`${p}/`));
  const currentTitle =
    routeTitles[
      Object.keys(routeTitles).find((path) => currentPath.startsWith(path))
    ] || '';
  const handleGoBack = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/contractor/dashboard');
  };

  const userName = user?.fullName || 'Contractor';
  const userInitials = useMemo(
    () =>
      String(userName)
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0]?.toUpperCase() || '')
        .join('') || 'C',
    [userName]
  );
  const userPhoto = resolveProfilePhoto(
    user?.photo,
    user?.photoUpdatedAt || user?.updatedAt || ''
  );

  return (
    <div
      className="panel-layout-shell"
      style={{
        display: 'flex',
        height: '100vh',
        '--role-sidebar-gradient': 'linear-gradient(180deg, #374151 0%, #6b7280 100%)',
        '--role-sidebar-border': '#1f2937',
        '--role-title-color': '#4b5563',
        '--panel-accent-rgb': '107, 114, 128',
        '--panel-nav-bg': '#374151',
        '--panel-nav-border': 'rgba(255, 255, 255, 0.18)',
        '--panel-nav-shadow': '0 -10px 30px rgba(55, 65, 81, 0.35)',
        '--panel-nav-active-bg': '#ffffff',
        '--panel-nav-active-color': '#374151',
        '--panel-nav-active-shadow': '0 2px 8px rgba(55, 65, 81, 0.24)',
      }}
    >
      <div
        className="d-none d-md-block"
        style={{ width: '240px', background: 'var(--role-sidebar-gradient)', height: '100vh', display: 'flex', flexDirection: 'column' }}
      >
        <div
          style={{
            color: 'white',
            fontSize: '22px',
            fontWeight: 'bold',
            padding: '8.5px',
            borderBottom: '2px solid var(--role-sidebar-border)',
            textAlign: 'center',
            letterSpacing: '1px',
          }}
        >
          🏭 Lotus Rice Mill Pvt. Ltd.
        </div>
        <div className="d-flex flex-column p-3" style={{ flex: 1, overflowY: 'auto' }}>
          <div className="panel-sidebar-menu">
            {menuItems.map((item) => (
              <button
                key={item.path}
                type="button"
                className={`panel-sidebar-item ${isBottomActive([item.path]) ? 'active' : ''}`}
                onClick={() => handleNav(item.path)}
              >
                <span className="material-symbols-outlined">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <Offcanvas show={sidebarVisible} onHide={() => setSidebarVisible(false)} className="panel-mobile-drawer">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>🏭 Lotus Rice Mill Pvt. Ltd.</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body style={{ background: 'linear-gradient(180deg, #374151 0%, #6b7280 100%)', color: '#fff' }}>
          <div className="panel-sidebar-menu">
            {menuItems.map((item) => (
              <button
                key={item.path}
                type="button"
                className={`panel-sidebar-item ${isBottomActive([item.path]) ? 'active' : ''}`}
                onClick={() => handleNav(item.path)}
              >
                <span className="material-symbols-outlined">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      <div className="panel-layout-maincol" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div
          className="panel-layout-topbar"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 24px',
            backgroundColor: 'var(--app-topbar)',
            borderBottom: '1px solid #dee2e6',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {!isDashboardRoute && (
              <Button variant="light" className="d-md-none" onClick={handleGoBack} aria-label="Go back">
                <span className="material-symbols-outlined">arrow_back_ios_new</span>
              </Button>
            )}
            <Button variant="light" className="d-md-none" onClick={() => setSidebarVisible(true)}>
              <BsList />
            </Button>
            <div className="panel-mobile-title" style={{ fontSize: '18px', fontWeight: 600, color: 'var(--role-title-color)' }}>
              {currentTitle}
            </div>
          </div>

          <Dropdown>
            <Dropdown.Toggle variant="light" size="sm" style={{ fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {userPhoto ? (
                <img src={userPhoto} alt={userName} style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover' }} />
              ) : (
                <span
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#e2e8f0',
                    color: '#334155',
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {userInitials}
                </span>
              )}
              <span className="panel-mobile-user-name">{userName}</span>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item href="/contractor/profile">Profile</Dropdown.Item>
              <Dropdown.Item onClick={handleLogout}>Logout</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <GstApiDueNotice />

        <div
          className="panel-layout-content"
          style={{
            flex: 1,
            overflowY: 'auto',
            background: 'var(--app-bg)',
            padding: '20px',
          }}
        >
          <Outlet />
        </div>

        <div className="panel-mobile-nav" style={{ '--panel-nav-count': 5 }}>
          <button
            className={`panel-mobile-nav-item ${isBottomActive(['/contractor/dashboard']) ? 'active' : ''}`}
            onClick={() => navigate('/contractor/dashboard')}
          >
            <span className="material-symbols-outlined">dashboard</span>
            <span>Dashboard</span>
          </button>
          <button
            className={`panel-mobile-nav-item ${isBottomActive(['/contractor/work-entry']) ? 'active' : ''}`}
            onClick={() => navigate('/contractor/work-entry')}
          >
            <span className="material-symbols-outlined">edit_note</span>
            <span>Add</span>
          </button>
          <button
            className={`panel-mobile-nav-item ${isBottomActive(['/contractor/work-history']) ? 'active' : ''}`}
            onClick={() => navigate('/contractor/work-history')}
          >
            <span className="material-symbols-outlined">history</span>
            <span>History</span>
          </button>
          <button
            className={`panel-mobile-nav-item ${isBottomActive(['/contractor/payment-history']) ? 'active' : ''}`}
            onClick={() => navigate('/contractor/payment-history')}
          >
            <span className="material-symbols-outlined">receipt_long</span>
            <span>Payments</span>
          </button>
          <button
            className={`panel-mobile-nav-item ${isBottomActive(['/contractor/profile']) ? 'active' : ''}`}
            onClick={() => navigate('/contractor/profile')}
          >
            <span className="material-symbols-outlined">person</span>
            <span>Profile</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContractorMainLayout;
